import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IndosbookLogo } from "./IndosbookLogo";
import { useAuth, api } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { rankOptions, vesselOptions } from "../mock";
import { ArrowLeft, Briefcase, Plus, Users, Loader2, ChevronRight, Ship, Award, Eye } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const STATUSES = [
  { id: "applied", label: "Applied" },
  { id: "under_review", label: "Under Review" },
  { id: "shortlisted", label: "Shortlisted" },
  { id: "offer", label: "Offer" },
  { id: "rejected", label: "Rejected" },
];
const STATUS_CLS = {
  applied: "bg-gray-100 text-gray-600", under_review: "bg-blue-50 text-blue-700",
  shortlisted: "bg-amber-50 text-amber-700", offer: "bg-green-50 text-green-700", rejected: "bg-red-50 text-red-700",
};
const EMPTY = { title: "", rank: "", vessel: "", salary: "", location: "", joining: "", body: "" };

const CompanyDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [posting, setPosting] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try { setJobs((await api.get("/company/jobs")).data.jobs); }
    catch { toast({ title: "Could not load jobs", variant: "destructive" }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => {
    if (user && user.role !== "company") { navigate("/feed"); return; }
    loadJobs();
  }, [user, loadJobs, navigate]);

  const postJob = async () => {
    if (!form.title.trim() || !form.rank || !form.vessel) { toast({ title: "Title, rank and vessel are required", variant: "destructive" }); return; }
    setPosting(true);
    try {
      await api.post("/jobs", form);
      setForm(EMPTY);
      toast({ title: "Job posted to the feed!" });
      loadJobs();
    } catch (e) { toast({ title: "Could not post job", description: e?.response?.data?.detail, variant: "destructive" }); }
    finally { setPosting(false); }
  };

  const openApplicants = async (job) => {
    setActiveJob(job); setLoadingApps(true);
    try { setApplicants((await api.get(`/company/applicants?job_post_id=${job.id}`)).data.applicants); }
    catch { toast({ title: "Could not load applicants", variant: "destructive" }); }
    finally { setLoadingApps(false); }
  };

  const changeStatus = async (a, status) => {
    try {
      const res = (await api.put(`/applications/${a.application_id}/status`, { status })).data;
      setApplicants((l) => l.map((x) => (x.application_id === a.application_id ? { ...x, status: res.status, status_label: res.status_label } : x)));
      toast({ title: `Marked as ${res.status_label}` });
    } catch { toast({ title: "Could not update", variant: "destructive" }); }
  };

  const viewProfile = async (a) => {
    setProfileOpen(true); setProfile(null);
    try { setProfile((await api.get(`/company/applicants/${a.application_id}/profile`)).data); }
    catch { toast({ title: "Could not load profile", variant: "destructive" }); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white">
        <div className="mx-auto flex h-16 max-w-[1000px] items-center gap-4 px-4 sm:px-6">
          <button onClick={() => navigate("/feed")} className="shrink-0"><IndosbookLogo size={28} /></button>
          <span className="flex items-center gap-2 text-sm font-bold text-gray-700"><Briefcase className="h-4 w-4 text-[#002855]" /> Company Dashboard</span>
          <Button variant="outline" onClick={() => navigate("/feed")} data-testid="dashboard-back-button" className="ml-auto rounded-full text-sm font-bold"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to Feed</Button>
        </div>
      </header>

      <div className="mx-auto grid max-w-[1000px] gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[380px_1fr]">
        {/* Post a job */}
        <section className="h-fit rounded-2xl border border-gray-200 bg-white p-6" data-testid="post-job-form">
          <div className="flex items-center gap-2 text-lg font-extrabold text-gray-900"><Plus className="h-5 w-5 text-[#FF4500]" /> Post a Job</div>
          <div className="mt-4 space-y-3">
            <div className="space-y-1.5"><Label>Job title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="2nd Officer — Oil Tanker" data-testid="job-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Rank *</Label>
                <Select value={form.rank} onValueChange={(v) => setForm({ ...form, rank: v })}>
                  <SelectTrigger data-testid="job-rank-select"><SelectValue placeholder="Rank" /></SelectTrigger>
                  <SelectContent className="max-h-56">{rankOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Vessel *</Label>
                <Select value={form.vessel} onValueChange={(v) => setForm({ ...form, vessel: v })}>
                  <SelectTrigger data-testid="job-vessel-select"><SelectValue placeholder="Vessel" /></SelectTrigger>
                  <SelectContent className="max-h-56">{vesselOptions.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Wages</Label><Input value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} placeholder="$5,800 / month" data-testid="job-salary-input" /></div>
              <div className="space-y-1.5"><Label>Joining</Label><Input value={form.joining} onChange={(e) => setForm({ ...form, joining: e.target.value })} placeholder="Within 30 days" data-testid="job-joining-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Worldwide" data-testid="job-location-input" /></div>
            <div className="space-y-1.5"><Label>Description</Label>
              <textarea rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="job-body-input"
                className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002855]/30" placeholder="Requirements, contract length, trading area…" /></div>
            <Button onClick={postJob} disabled={posting} data-testid="post-job-button" className="w-full rounded-full bg-[#FF4500] font-bold hover:bg-[#e03d00]">
              {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post Job"}
            </Button>
          </div>
        </section>

        {/* Jobs + applicants */}
        <section className="space-y-4">
          {!activeJob && (
            <div className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="company-jobs-list">
              <div className="text-lg font-extrabold text-gray-900">Your Job Postings ({jobs.length})</div>
              <div className="mt-4 space-y-3">
                {loading && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#FF4500]" /></div>}
                {!loading && jobs.length === 0 && <p className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-400" data-testid="jobs-empty">No jobs yet. Post your first vacancy on the left.</p>}
                {jobs.map((j) => (
                  <button key={j.id} onClick={() => openApplicants(j)} data-testid={`company-job-${j.id}`}
                    className="flex w-full items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/60 p-4 text-left hover:bg-gray-100">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100"><Briefcase className="h-5 w-5 text-[#002855]" /></span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-bold text-gray-900">{j.title}</div>
                      <div className="text-xs text-gray-400">{j.job?.rank} · {j.job?.vessel} · posted {j.time} ago</div>
                    </div>
                    <span className="flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-bold text-[#002855] border border-gray-200"><Users className="h-3.5 w-3.5" /> {j.applicant_count}</span>
                    <ChevronRight className="h-4 w-4 text-gray-300" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeJob && (
            <div className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="applicants-panel">
              <div className="flex items-center gap-2">
                <button onClick={() => setActiveJob(null)} data-testid="applicants-back" className="rounded-full p-1 hover:bg-gray-100"><ArrowLeft className="h-4 w-4" /></button>
                <div className="min-w-0">
                  <div className="truncate text-lg font-extrabold text-gray-900">{activeJob.title}</div>
                  <div className="text-xs text-gray-400">{applicants.length} applicant{applicants.length === 1 ? "" : "s"}</div>
                </div>
              </div>
              <div className="mt-4 space-y-3">
                {loadingApps && <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#FF4500]" /></div>}
                {!loadingApps && applicants.length === 0 && <p className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-400" data-testid="applicants-empty">No applicants yet.</p>}
                {applicants.map((a) => (
                  <div key={a.application_id} className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/60 p-4" data-testid={`applicant-${a.application_id}`}>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#002855] text-sm font-black text-white">{(a.applicant.name || "S").charAt(0).toUpperCase()}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-bold text-gray-900">{a.applicant.name}</div>
                      <div className="truncate text-xs text-gray-400 capitalize">{a.applicant.rank || a.applicant.role} · {a.applicant.email}</div>
                    </div>
                    <button onClick={() => viewProfile(a)} data-testid={`view-profile-${a.application_id}`} className="flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-white"><Eye className="h-3.5 w-3.5" /> Profile</button>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_CLS[a.status]}`}>{a.status_label}</span>
                    <Select value={a.status} onValueChange={(v) => changeStatus(a, v)}>
                      <SelectTrigger className="h-9 w-[150px]" data-testid={`status-select-${a.application_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-lg" data-testid="applicant-profile-dialog">
          <DialogHeader>
            <DialogTitle>Applicant Profile</DialogTitle>
            <DialogDescription className="sr-only">Full profile and sea service record</DialogDescription>
          </DialogHeader>
          {!profile ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#FF4500]" /></div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#002855] text-xl font-black text-white">{(profile.profile.name || "S").charAt(0).toUpperCase()}</span>
                <div>
                  <div className="text-lg font-extrabold text-gray-900" data-testid="profile-dialog-name">{profile.profile.name}</div>
                  <div className="text-sm capitalize text-gray-500">{profile.profile.rank || profile.profile.role} · {profile.profile.email}</div>
                  <span className="mt-1 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700">{profile.status_label}</span>
                </div>
              </div>
              {profile.profile.bio && <p className="rounded-xl bg-gray-50 p-3 text-sm text-gray-600">{profile.profile.bio}</p>}

              <div>
                <div className="flex items-center gap-2 text-sm font-extrabold text-gray-900"><Ship className="h-4 w-4 text-[#002855]" /> Sea Service ({profile.sea_service.length})</div>
                <div className="mt-2 space-y-2">
                  {profile.sea_service.length === 0 && <p className="text-xs text-gray-400">No sea service records shared.</p>}
                  {profile.sea_service.map((s) => (
                    <div key={s.id} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3 text-sm">
                      <div className="font-bold text-gray-800">{s.vessel_name} <span className="font-normal text-gray-500">· {s.rank}</span></div>
                      <div className="text-xs text-gray-400">{s.company} · {s.vessel_type} · {s.sign_on} → {s.sign_off || "present"}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 text-sm font-extrabold text-gray-900"><Award className="h-4 w-4 text-[#002855]" /> Certificates ({profile.certificates.length})</div>
                <div className="mt-2 space-y-2">
                  {profile.certificates.length === 0 && <p className="text-xs text-gray-400">No certificates shared.</p>}
                  {profile.certificates.map((c) => (
                    <div key={c.id} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/60 p-3 text-sm">
                      <div><div className="font-bold text-gray-800">{c.name}</div><div className="text-xs text-gray-400">{c.cert_number || "—"} · exp {c.expiry_date || "n/a"}</div></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyDashboard;
